// createType — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-type' })` op (DSE2 §3.D5).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D4 — Type / Domain UI.
//
// Per-engine surface (per DSE2 dialect.userDefinedType):
//   - mssql:  alias  → CREATE TYPE name FROM <baseType>
//             table  → CREATE TYPE name AS TABLE (<columns>)
//   - pgsql:  enum   → CREATE TYPE name AS ENUM ('a', 'b', …)
//             composite → CREATE TYPE name AS (col1 type1, …)
//             domain → CREATE DOMAIN name AS <baseType> [DEFAULT …] [NOT NULL] [CHECK …]
//             range  → CREATE TYPE name AS RANGE (subtype = …)
//   - oracle: object → CREATE [OR REPLACE] TYPE name AS OBJECT (attrs)
//             table-of → CREATE [OR REPLACE] TYPE name AS TABLE OF <baseType>
//             varray → CREATE [OR REPLACE] TYPE name AS VARRAY(n) OF <baseType>
//   - mysql + sqlite: unsupported.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  UserDefinedTypeDef,
  UserDefinedTypeKind,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateTypeArgs {
  readonly userType: UserDefinedTypeDef;
}

export function buildCreateTypeEdit(args: CreateTypeArgs): DdlEdit {
  return { op: 'create-type', userType: args.userType };
}

export async function commitCreateType(
  ctx: VqeDdlEditContext,
  args: CreateTypeArgs,
): Promise<void> {
  const edit = buildCreateTypeEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const TYPE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'oracle',
] as const);

export function isCreateTypeSupported(engine: SqlEngine): boolean {
  return TYPE_SUPPORTED.has(engine);
}

export function getCreateTypeUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mysql') {
    return (
      'MySQL has no user-defined type system. Use ENUM(\'a\',\'b\',…) / SET(…) '
      + 'column types directly via Add Column for enum-like fields.'
    );
  }
  if (engine === 'sqlite') {
    return (
      'SQLite has no user-defined type system. Use a CHECK constraint on the '
      + 'column to restrict allowed values.'
    );
  }
  return '';
}

const ENGINE_KIND_SUPPORT: Record<SqlEngine, ReadonlyArray<UserDefinedTypeKind>> = {
  mssql: ['alias', 'table'],
  postgres: ['enum', 'composite', 'domain', 'range'],
  mysql: [],
  oracle: ['object', 'table-of', 'varray'],
  sqlite: [],
};

export function getSupportedKindsForEngine(
  engine: SqlEngine,
): ReadonlyArray<UserDefinedTypeKind> {
  return ENGINE_KIND_SUPPORT[engine];
}

export function isKindSupportedForEngine(
  engine: SqlEngine,
  kind: UserDefinedTypeKind,
): boolean {
  return ENGINE_KIND_SUPPORT[engine].includes(kind);
}

export function getKindUnsupportedTooltip(
  engine: SqlEngine,
  kind: UserDefinedTypeKind,
): string {
  if (!isCreateTypeSupported(engine)) {
    return getCreateTypeUnsupportedTooltip(engine);
  }
  if (engine === 'mssql') {
    if (kind === 'enum') return 'mssql has no enum type. Use a CHECK constraint or a lookup table.';
    if (kind === 'composite') return 'mssql has no composite type. Use kind:\'table\' (TVP) for the closest equivalent.';
    if (kind === 'domain') return 'mssql has no CREATE DOMAIN. Use kind:\'alias\' for the closest equivalent.';
    if (kind === 'range') return 'mssql has no range type.';
    return 'mssql does not support this kind.';
  }
  if (engine === 'postgres') {
    if (kind === 'alias') return 'pgsql has no CREATE TYPE FROM alias. Use kind:\'domain\' to wrap a base type with constraints.';
    if (kind === 'table') return 'pgsql has no TVP. Use kind:\'composite\' for a row-shaped type.';
    return 'pgsql does not support this kind.';
  }
  if (engine === 'oracle') {
    if (kind === 'enum') return 'oracle has no enum type. Use a CHECK constraint or a lookup table.';
    if (kind === 'composite') return 'oracle has no pgsql-style composite type. Use kind:\'object\' for an attribute-bearing type.';
    if (kind === 'domain') return 'oracle has no CREATE DOMAIN. Use SUBTYPE inside a PL/SQL package, or a CHECK constraint.';
    if (kind === 'range') return 'oracle has no range type.';
    if (kind === 'alias') return 'oracle has no CREATE TYPE FROM alias.';
    if (kind === 'table') return 'oracle has no TVP. Use kind:\'object\' or kind:\'table-of\' for nested-table types.';
    return 'oracle does not support this kind.';
  }
  return '';
}

/** Pre-flight validator mirroring DSE2's per-engine reject paths. Returns
 *  `null` when args are acceptable, else a user-facing message. */
export function validateCreateTypeForEngine(
  engine: SqlEngine,
  args: CreateTypeArgs,
): string | null {
  if (!isCreateTypeSupported(engine)) {
    return getCreateTypeUnsupportedTooltip(engine);
  }
  const t = args.userType;
  if (t.name.name.trim().length === 0) {
    return 'Type name is required.';
  }
  if (!isKindSupportedForEngine(engine, t.kind)) {
    return getKindUnsupportedTooltip(engine, t.kind);
  }
  if (t.kind === 'enum') {
    if (t.values.length === 0) return 'Enum types require at least one value.';
  }
  if (t.kind === 'composite') {
    if (t.attributes.length === 0) return 'Composite types require at least one attribute.';
    for (const a of t.attributes) {
      if (a.name.trim().length === 0 || a.typeRef.trim().length === 0) {
        return 'Each composite attribute requires a name and a type.';
      }
    }
  }
  if (t.kind === 'domain') {
    if (t.baseType.trim().length === 0) return 'Domain types require a base type.';
  }
  if (t.kind === 'range') {
    if (t.subtype.trim().length === 0) return 'Range types require a subtype.';
  }
  if (t.kind === 'alias') {
    if (t.baseType.trim().length === 0) return 'Alias types require a base type.';
  }
  if (t.kind === 'object') {
    if (t.attributes.length === 0) return 'Object types require at least one attribute.';
  }
  if (t.kind === 'table-of') {
    if (t.baseType.trim().length === 0) return 'Nested-table types require a base type.';
  }
  if (t.kind === 'varray') {
    if (!Number.isFinite(t.size) || t.size <= 0) return 'VARRAY size must be a positive integer.';
    if (t.baseType.trim().length === 0) return 'VARRAY types require a base type.';
  }
  return null;
}
